import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { helper } from 'src/environments/helper';
import { DocumenteCetateanService } from '../documente-cetatean.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-edit-fisiere-document-cetatean',
    templateUrl: './edit-fisiere-document-cetatean.component.html',
    styleUrls: ['./edit-fisiere-document-cetatean.component.scss']
})
export class EditFisiereDocumentCetateanComponent {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;

    token!: string;
    isLoading = false;
    fileDeleted = false;

    uploading: any = {
        file: false,
    };

    files: any = {
        file: [],
    };

    form: FormGroup = new FormGroup({
        existingFiles: this.fb.array(this.data.files),
        files: this.fb.array([], [Validators.required])
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<EditFisiereDocumentCetateanComponent>,
        private documenteCetateanService: DocumenteCetateanService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private fb: FormBuilder,
        public toastr: ToastrService,
    ) {
        this.processToken();
    }

    closeDialog() {
        if (this.fileDeleted) {
            this.dialogRef?.close({
                success: true,
            });
        } else {
            this.dialogRef?.close({
                success: false,
            });
        }
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.documenteCetateanService.setToken(token);
        });
    }

    addFile(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.documenteCetateanService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response = typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            const files = this.form.get(
                                'files'
                            ) as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;
                            this.openToast({
                                title: this.successTitle,
                                message:
                                    helper.dialogConfig.generalMessages.fileSuccessfullyUploaded,
                                type: this.successIcon,
                            });
                        }
                    } else {
                        // Handle error cases here
                        let errorMessage =
                            environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }
    }

    removeFile(index: number) {
        const files = this.form.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    removeExistingFile(index: number) {
        const files = this.form.get('existingFiles') as FormArray;
        if (index > -1 && index < files.length) {
            const fileId = files.at(index).value.id_fisier;
            Swal.fire({
                title: this.helper.swalTextConfig.commonTitles.areYouSure,
                text: 'O să ștergeți acest fișier din document!',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText:
                    this.helper.swalTextConfig.validationMessages.confirmButton,
                cancelButtonText:
                    this.helper.swalTextConfig.validationMessages.cancelButton,
                showLoaderOnConfirm: true,
                reverseButtons: true,
                preConfirm: () => {
                    return this.documenteCetateanService
                        .deleteFiles({ file_ids: [fileId] })
                        .toPromise()
                        .then((response) => {
                            return response;
                        })
                        .catch((error) => {
                            Swal.showValidationMessage(`Request failed: ${error}`);
                        });
                },
                allowOutsideClick: () => !Swal.isLoading(),
            }).then((result) => {
                if (result.isConfirmed) {
                    Swal.fire({
                        title: this.helper.swalTextConfig.headers.success,
                        text: 'Fișierul a fost șters cu succes!',
                        icon: 'success',
                        confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                    })
                    files.removeAt(index);
                    this.fileDeleted = true;
                }
            });
        }
    }

    submitForm() {
        this.isLoading = true;

        const data = { files: this.form.get('files')?.value };

        this.documenteCetateanService
            .addFiles(this.data.id_document, data)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({
                        title: this.errorTitle,
                        message: msg,
                        type: this.errorType,
                    });
                } else {
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                }
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }
}
